// utils/statusUtils.js
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../firebase';

/**
 * Record a status change for a package
 * @param {string} packageId - The ID of the package
 * @param {string} newStatus - The new status value
 * @param {string} oldStatus - The previous status value (optional)
 * @param {string} assignedTo - User ID the package is assigned to (optional)
 */
export const recordStatusChange = async (packageId, newStatus, oldStatus = null, assignedTo = null) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      console.warn('No authenticated user found for status change recording');
      return;
    }
    
    // Create the status change record
    const statusChangeData = {
      packageId,
      status: newStatus,
      previousStatus: oldStatus,
      assignedTo,
      changedBy: user.uid,
      timestamp: serverTimestamp(),
      createdAt: serverTimestamp()
    };
    
    // Add the status change to the statusChanges collection
    await addDoc(collection(db, 'statusChanges'), statusChangeData);
    
    console.log('Status change recorded:', {
      packageId,
      from: oldStatus,
      to: newStatus,
      by: user.uid
    });
  } catch (error) {
    console.error('Error recording status change:', error);
    // Don't throw the error since status change recording is not critical
    // The main operation (package update/payment) should still succeed
  }
};

/**
 * Record multiple status changes at once (batch operation)
 * @param {Array} statusChanges - Array of status change objects
 */
export const recordMultipleStatusChanges = async (statusChanges) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      console.warn('No authenticated user found for batch status change recording');
      return;
    }
    
    const promises = statusChanges.map(change => {
      return recordStatusChange(
        change.packageId,
        change.newStatus,
        change.oldStatus,
        change.assignedTo
      );
    });
    
    await Promise.all(promises);
    console.log(`Recorded ${statusChanges.length} status changes`);
  } catch (error) {
    console.error('Error recording multiple status changes:', error);
  }
};

/**
 * Get status display information
 * @param {string} status - The status string
 * @returns {Object} Status display information with label, color, and icon
 */
export const getStatusInfo = (status) => {
  const statusTypes = {
    'delivered': { 
      label: 'Delivered', 
      color: 'bg-green-100 text-green-800',
      icon: (
        <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
      )
    },
    'paid': { 
      label: 'Paid', 
      color: 'bg-emerald-100 text-emerald-800',
      icon: (
        <svg className="w-4 h-4 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
        </svg>
      )
    },
    'in-transit': { 
      label: 'In Transit', 
      color: 'bg-blue-100 text-blue-800',
      icon: (
        <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
        </svg>
      )
    },
    'shipped': { 
      label: 'Shipped', 
      color: 'bg-purple-100 text-purple-800',
      icon: (
        <svg className="w-4 h-4 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 11l7-7 7 7M5 19l7-7 7 7" />
        </svg>
      )
    },
    'pending': { 
      label: 'Pending', 
      color: 'bg-gray-100 text-gray-800',
      icon: (
        <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    },
    'delayed': { 
      label: 'Delayed', 
      color: 'bg-yellow-100 text-yellow-800',
      icon: (
        <svg className="w-4 h-4 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      )
    },
    'returned': { 
      label: 'Returned', 
      color: 'bg-red-100 text-red-800',
      icon: (
        <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
        </svg>
      )
    }
  };

  // Default to pending if status not found
  return statusTypes[status?.toLowerCase()] || statusTypes.pending;
};

/**
 * Format status for display (capitalize each word)
 * @param {string} status - The status string
 * @returns {string} Formatted status string
 */
export const formatStatus = (status) => {
  if (!status) return 'Pending';
  
  return status
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Get all available status options
 * @returns {Array} Array of status objects with value and label
 */
export const getStatusOptions = () => {
  return [
    { value: 'pending', label: 'Pending' },
    { value: 'shipped', label: 'Shipped' },
    { value: 'in-transit', label: 'In Transit' },
    { value: 'delivered', label: 'Delivered' },
    { value: 'delayed', label: 'Delayed' },
    { value: 'returned', label: 'Returned' },
    { value: 'paid', label: 'Paid' }
  ];
};

/**
 * Check if a status transition is valid
 * @param {string} fromStatus - Current status
 * @param {string} toStatus - New status
 * @returns {boolean} Whether the transition is valid
 */
export const isValidStatusTransition = (fromStatus, toStatus) => {
  // Define valid status transitions
  const validTransitions = {
    'pending': ['shipped', 'delayed', 'returned'],
    'shipped': ['in-transit', 'delivered', 'delayed', 'returned'],
    'in-transit': ['delivered', 'delayed', 'returned'],
    'delivered': ['returned'], // Can only return from delivered
    'delayed': ['shipped', 'in-transit', 'delivered', 'returned'],
    'returned': ['pending', 'shipped'], // Can restart process after return
    'paid': [] // Paid is typically a final state
  };

  // Allow any transition to paid status (payments can be made at any time)
  if (toStatus === 'paid') {
    return true;
  }

  // Check if the transition is in the valid transitions list
  const allowedTransitions = validTransitions[fromStatus?.toLowerCase()] || [];
  return allowedTransitions.includes(toStatus?.toLowerCase());
};

/**
 * Get status priority for sorting (lower number = higher priority)
 * @param {string} status - The status string
 * @returns {number} Priority number for sorting
 */
export const getStatusPriority = (status) => {
  const priorities = {
    'delayed': 1,     // Highest priority
    'returned': 2,
    'pending': 3,
    'shipped': 4,
    'in-transit': 5,
    'delivered': 6,
    'paid': 7        // Lowest priority (completed)
  };

  return priorities[status?.toLowerCase()] || 99;
};